import React, { useEffect, useState } from 'react';
import * as SimpleWebAuthnBrowser from '@simplewebauthn/browser';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Field, Formik } from 'formik';
import moment from 'moment';
import { Button, Form, Modal } from 'react-bootstrap';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import ReactstrapFormikFloatingLabelInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelInput';
import SelectBox from '../../components/reactstrap-formik/SelectBox';
import type { IEmailLoginForm } from '../../models/login';
import type { IRegionData } from '../../models/profile';
import {
  checkPasskeySetupStatus,
  loginWithPasskey,
  signInConfirmWithPasskey,
} from '../../services/login/login-service';
import { getRegions } from '../../services/login/loginApi-service';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch, store } from '../../store';
import { changeLanguage, logout, saveUserData, setCurrencyDetails, userDetail } from '../../store/actions/app.Actions';
import { ILoginProps } from '../../types/propTypes';
import { companyNameReplace } from '../../utils';
import { Common } from '../../utils/constants';
import { LoginValidationSchema } from '../../validation/login/login-validation';
import SocialMediaLoginForm from './SocialMediaLoginForm';
import { env } from 'config/env';

const LoginForm = ({ setEmail, onChangeStage, handleChangeFormValues }: ILoginProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const parameters = new URLSearchParams(window.location.search);

  const [cookies, _setCookie] = useCookies(['driver_remember', 'driver_email', 'driver_password']);
  const [selectedRegion, setSelectedRegion] = useState<IRegionData | null>(null);
  const [regions, setRegions] = useState<Array<IRegionData>>([]);

  const storedData = localStorage.getItem(Common.RESET_AUTH_METHOD);
  const { email } = storedData ? JSON.parse(storedData) : { email: '' };

  const [stage, _setStage] = useState<number>(
    parameters.get('stage') && parameters.get('stage') !== 'undefined'
      ? Number(parameters.get('stage'))
      : Common.AuthStage.SIGNIN,
  );

  const [showPasskeyFailModal, setShowPasskeyFailModal] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    getRegion(i18n.language);
  }, [i18n.language]);

  const colorStyles = {
    control: (base) => ({
      ...base,
      border: 0,
      borderColor: '#CBCED7',
      boxShadow: 'none',
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? 'var(--themeColor)' : null,
      color: isFocused ? 'white' : '#333333',
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--themeColor)',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '0',
      color: '#929292',
      position: 'relative',
      right: '10px',
    }),
    valueContainer: (base) => ({
      ...base,
    }),
    menu: (base) => ({
      ...base,
      minWidth: '110px',
      zIndex: '999',
      margin: 0,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10000,
    }),
    menuList: (base) => ({
      ...base,
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: 'grey transparent',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'grey',
        borderRadius: '3px',
      },
      zIndex: '10000',
    }),
  };

  const rememberValue = cookies.driver_remember === 'true';

  const [initialState, setInitialState] = useState<IEmailLoginForm>({
    email: rememberValue
      ? cookies.driver_email
      : parameters?.get('email') && parameters?.get('email') !== 'undefined'
        ? `${parameters.get('email')}`
        : email,
  });

  const getRegion = async (language?: string) => {
    try {
      await getRegions(language).then((res) => {
        if (res && res.data && res.data.rows) {
          setRegions(res.data.rows);
          setSelectedRegion(res.data.rows && res.data.rows.filter((item) => item.code === env.VITE_PORTAL_REGION)[0]);
        } else {
          setRegions([]);
          setSelectedRegion(null);
        }
      });
    } catch (_e) {
      setRegions([]);
      setSelectedRegion(null);
    }
  };

  const checkPasskeySetup = async (value: IEmailLoginForm) => {
    setLoading(true);
    setInitialState((prevValue) => ({
      ...prevValue,
      email: value.email,
    }));
    try {
      const response = await checkPasskeySetupStatus(value.email);
      const assertion = await SimpleWebAuthnBrowser.startAuthentication(response.Data);

      const params = { email: value.email, credential: assertion };
      await loginWithPasskey(params);

      const confirmSignInResponse = await signInConfirmWithPasskey({ params: params });

      if (
        !confirmSignInResponse?.isSignedIn ||
        confirmSignInResponse.nextStep.signInStep !== Common.AuthenticationSteps.DONE
      ) {
        toast.error(t('login.toast.error'));
        return;
      }

      if (
        confirmSignInResponse?.isSignedIn &&
        confirmSignInResponse.nextStep.signInStep === Common.AuthenticationSteps.DONE
      ) {
        const authSession = await fetchAuthSession();
        const authResponse = { ...confirmSignInResponse, authSession };
        await handleUserProfileAfterPasskeyLogin(authResponse);
      }
    } catch (error) {
      setLoading(false);
      const name = error?.name || '';
      const message = error?.message || '';

      if (name === 'NotAllowedError') {
        setShowPasskeyFailModal(true);
        return;
      }

      if (name === 'AbortError') {
        setShowPasskeyFailModal(true);
        return;
      }

      if (name === 'SecurityError') {
        toast.error(message);
        return;
      }

      if (error?.response?.data?.error_code === 'PasskeyNotConfigured') {
        setEmail(value.email);
        onChangeStage(Common.AuthStage.SIGNIN_WITHOUT_PASSKEY);
        return;
      }

      toast.error(error?.response?.data?.message || error?.message || error?.toString());
    }
  };

  const handleUserProfileAfterPasskeyLogin = async (authResponse) => {
    const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';
    getProfile()
      .then(async (response) => {
        if (response.Data?.status?.code === Common.UserStatus.CANCELLED) {
          dispatch(logout()).then(() => {
            toast.error(t('login.toast.cancelled'), { autoClose: 15000 });
          });
        } else {
          dispatch(saveUserData({ ...authResponse, ...response.Data }));
          dispatch(changeLanguage(response?.Data?.language_preference));
          dispatch(setCurrencyDetails(response?.Data?.default_currency));
          dispatch(userDetail(response?.Data));
          moment.locale(store.getState().UserData?.language?.code);

          if (downloadVATRedirect) {
            sessionStorage.removeItem('vat_receipt_path');
            navigate(downloadVATRedirect);
            return;
          }
          if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
            const referralCode = localStorage.getItem('referralCode');
            navigate(Common.Routes.REFERRAL_PROMOTION_PREFERENCE, {
              state: { referralCode: referralCode },
              replace: true,
            });
            return;
          }
          if (!response?.Data.is_profile_verified) {
            navigate(Common.Routes.PROFILE_REDIRECT);
            return;
          }

          if (response?.Data.payment_type === null) {
            navigate(Common.Routes.PAYMENT);
            return;
          }

          navigate(Common.Routes.HOME);
        }
      })
      .catch(() => {
        dispatch(logout());
        toast.error(t('login.toast.error'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRegionChange = (selectedRegion: IRegionData) => {
    setSelectedRegion(selectedRegion);
    if (stage === Common.AuthStage.SIGNUP) {
      window.location.href = `${selectedRegion.driver_portal_host_url}/login?stage=3&email=${initialState?.email}`;
    } else if (stage === Common.AuthStage.SIGNIN) {
      window.location.href = `${selectedRegion.driver_portal_host_url}/login?stage=0&email=${initialState?.email}`;
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInitialState((prevVal) => ({
      ...prevVal,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <>
      <div className="signin-form-container font-poppins">
        <Helmet>
          <title>{companyNameReplace(t('login.title'))}</title>
        </Helmet>
        <div className="sign-up-header">
          <div className="sign-up-icon-container">
            <img src={images.loginIconSVG} alt={companyNameReplace(t<string>('login.label.logo'))} />
          </div>
          <h1 className="title sign-in-title mt-3">{t('login.header')}</h1>
          <p className="sign-up-sub-header">{t('login.subHeader')}</p>
        </div>
        <div className="sign-up-select-region mt-2">
          <SelectBox
            styles={colorStyles}
            name="region"
            menuPlacement="bottom"
            menuPosition="fixed"
            value={selectedRegion}
            options={regions}
            onChange={(e: IRegionData) => {
              handleRegionChange(e);
            }}
            getOptionLabel={(opt: IRegionData) => opt.name as string}
            getOptionValue={(opt: IRegionData) => opt.id as number}
            isClearable={false}
            placeholder={t('login.placeholder.region')}
            formatOptionLabel={(option: IRegionData) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img
                  src={`${env.VITE_DRIVER_PORTAL_URL}${option.image_url}`}
                  alt={option.name}
                  style={{ width: '20px', height: '15px', objectFit: 'cover' }}
                />
                <span>{option.name}</span>
              </div>
            )}
            arrowSize="large"
            menuPortalTarget={document.body}
          />
        </div>
        <Formik initialValues={initialState} onSubmit={checkPasskeySetup} validationSchema={LoginValidationSchema(t)}>
          {({ handleSubmit }) => {
            return (
              <form className="form-secondary" onSubmit={handleSubmit} autoComplete="off" noValidate>
                <div className="mt-4 mb-4">
                  <Field
                    type="text"
                    name="email"
                    id="Email"
                    label={t('login.form.email')}
                    component={ReactstrapFormikFloatingLabelInput}
                    maxLength={50}
                    onKeyUp={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleChangeFormValues(e);
                      handleValueChange(e);
                    }}
                    autoComplete="username"
                    rightIcon={images.mailSVG}
                  />
                </div>
                <Form.Group className="form-group">
                  <Button type="submit" className="btn btn-primary w-100 sign-up-button" disabled={loading}>
                    {t('login.button.submit')}
                  </Button>
                </Form.Group>
                <div className="clearfix">
                  <p className="sign-up sign-up-text">
                    {t('login.label.newAccount')}{' '}
                    <a
                      href="#"
                      title={t<string>('login.button.register')}
                      onClick={(e) => {
                        e.preventDefault();
                        onChangeStage(Common.AuthStage.SIGNUP);
                      }}
                      className="signup-btn"
                    >
                      {t('login.button.register')}
                    </a>
                  </p>
                </div>
                <SocialMediaLoginForm action={'login'} />
              </form>
            );
          }}
        </Formik>
      </div>

      <Modal show={showPasskeyFailModal} centered className="passkey-fail-modal-container font-poppins">
        <Modal.Body className="modalBody">
          <button type="button" className="close" onClick={() => setShowPasskeyFailModal(false)}>
            <span aria-hidden="true" className="passkey-fail-modal-cross">
              ×
            </span>
            <span className="sr-only">Close</span>
          </button>
          <div className="passkey-fail-modal">
            <div className="biometric-image-container">
              <img src={images.biometricIconSVG} alt="" />
            </div>
            <p className="passkey-fail-header">{t('login.passKeyFailedModal.header')}</p>
            <p className="passkey-fail-text">{t('login.passKeyFailedModal.subHeader')}</p>
            <Button
              className="btn btn-primary passkey-try-again"
              onClick={() => {
                setShowPasskeyFailModal(false);
                checkPasskeySetup(initialState);
              }}
              title={t<string>('login.passKeyFailedModal.button.tryAgain')}
            >
              <span>{t('login.passKeyFailedModal.button.tryAgain')}</span>
            </Button>
            <a
              className="passkey-fail-use-password-link"
              onClick={() => {
                setEmail(initialState.email);
                onChangeStage(Common.AuthStage.SIGNIN_WITHOUT_PASSKEY);
                localStorage.setItem(Common.SHOW_UPDATE_MFA_MODAL, 'true');
                localStorage.setItem(Common.IS_PASSKEY_FAILED, 'true');
              }}
              title={t<string>('login.passKeyFailedModal.button.usePasswordText')}
            >
              <span className="passkey-fail-use-password font-poppins">
                {t('login.passKeyFailedModal.button.usePasswordText')}
              </span>
            </a>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LoginForm;
